/**
 * Shared Directus CMS health checks for prebuild verification and cron monitoring.
 */

export type DirectusHealthResult =
  | { ok: true; publishedCount: number; sampleTitle?: string }
  | {
      ok: false;
      reason:
        | "missing_config"
        | "cloudflare_challenge"
        | "invalid_credentials"
        | "http_error"
        | "invalid_json"
        | "empty_cms"
        | "network_error";
      detail: string;
      httpStatus?: number;
    };

export function normaliseDirectusBaseUrl(
  value: string | undefined
): string | undefined {
  if (!value) return undefined;
  return value.trim().replace(/\/$/, "");
}

export function isCloudflareChallengeBody(body: string): boolean {
  const lower = body.toLowerCase();
  return (
    lower.includes("just a moment") ||
    lower.includes("cf-chl") ||
    lower.includes("challenges.cloudflare.com") ||
    lower.includes("enable javascript and cookies")
  );
}

export function isCloudflareChallengeError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;

  const record = error as Record<string, unknown>;
  const errors = record.errors;
  if (typeof errors === "string" && isCloudflareChallengeBody(errors)) {
    return true;
  }

  const message = String(record.message ?? "");
  if (isCloudflareChallengeBody(message)) return true;

  const response = record.response as { status?: number; headers?: Headers } | undefined;
  if (response?.status === 403) {
    const server = response.headers?.get?.("server")?.toLowerCase();
    if (server === "cloudflare") return true;
  }

  return false;
}

export function formatDirectusFetchFailure(context: string, error: unknown): string {
  if (isCloudflareChallengeError(error)) {
    return `[${context}] Directus blocked by Cloudflare bot protection — add WAF skip for Bearer-authenticated /items/* and /files/* on directus.madebysdk.com. See docs/DIRECTUS_CLOUDFLARE_RUNBOOK.md`;
  }
  return `[${context}] Error fetching from Directus: ${String(error)}`;
}

type CheckDirectusOptions = {
  directusUrl?: string;
  directusToken?: string;
  originAuthSecret?: string;
  /** When true, zero published posts is treated as failure. Default true. */
  requirePublishedPosts?: boolean;
};

/**
 * Probes Directus REST API the same way Vercel build/runtime does.
 */
export async function checkDirectusHealth(
  options: CheckDirectusOptions = {}
): Promise<DirectusHealthResult> {
  const directusUrl = normaliseDirectusBaseUrl(
    options.directusUrl ??
      process.env.NEXT_PUBLIC_DIRECTUS_URL ??
      process.env.DIRECTUS_URL
  );
  const directusToken = options.directusToken ?? process.env.DIRECTUS_TOKEN?.trim();
  const originAuthSecret =
    options.originAuthSecret ?? process.env.ORIGIN_AUTH_SECRET?.trim();
  const requirePublishedPosts = options.requirePublishedPosts ?? true;

  if (!directusUrl || !directusToken) {
    return {
      ok: false,
      reason: "missing_config",
      detail: "DIRECTUS_URL and DIRECTUS_TOKEN must be set",
    };
  }

  const url = new URL(`${directusUrl}/items/posts`);
  url.searchParams.set("filter[status][_eq]", "published");
  url.searchParams.set("limit", "1");
  url.searchParams.set("fields", "id,title");
  url.searchParams.set("meta", "filter_count");

  const requestHeaders: Record<string, string> = {
    Authorization: `Bearer ${directusToken}`,
    Accept: "application/json",
  };
  if (originAuthSecret) {
    requestHeaders["X-Origin-Auth"] = originAuthSecret;
  }

  let response: Response;
  try {
    response = await fetch(url.toString(), {
      headers: requestHeaders,
      cache: "no-store",
    });
  } catch (error) {
    return {
      ok: false,
      reason: "network_error",
      detail: error instanceof Error ? error.message : String(error),
    };
  }

  const body = await response.text();

  if (isCloudflareChallengeBody(body)) {
    return {
      ok: false,
      reason: "cloudflare_challenge",
      httpStatus: response.status,
      detail:
        "Cloudflare returned a bot challenge (403/HTML). Configure WAF skip — see docs/DIRECTUS_CLOUDFLARE_RUNBOOK.md",
    };
  }

  if (response.status === 401) {
    return {
      ok: false,
      reason: "invalid_credentials",
      httpStatus: 401,
      detail: "DIRECTUS_TOKEN rejected (401 INVALID_CREDENTIALS). Rotate token in Directus and Vercel.",
    };
  }

  if (!response.ok) {
    return {
      ok: false,
      reason: "http_error",
      httpStatus: response.status,
      detail: `Directus returned HTTP ${response.status}: ${body.slice(0, 200)}`,
    };
  }

  let parsed: {
    data?: Array<{ id?: number; title?: string }>;
    meta?: { filter_count?: number };
  };
  try {
    parsed = JSON.parse(body) as typeof parsed;
  } catch {
    return {
      ok: false,
      reason: "invalid_json",
      httpStatus: response.status,
      detail: `Expected JSON from Directus, got: ${body.slice(0, 120)}`,
    };
  }

  const publishedCount =
    typeof parsed.meta?.filter_count === "number"
      ? parsed.meta.filter_count
      : Array.isArray(parsed.data)
        ? parsed.data.length
        : 0;

  if (requirePublishedPosts && publishedCount === 0) {
    return {
      ok: false,
      reason: "empty_cms",
      detail: "Directus returned zero published posts",
    };
  }

  return {
    ok: true,
    publishedCount,
    sampleTitle: parsed.data?.[0]?.title,
  };
}

export async function checkRssFeedHealth(
  siteUrl: string = process.env.SITE_URL ?? "https://hipelife.com"
): Promise<{ ok: boolean; itemCount: number; detail?: string }> {
  const rssUrl = `${siteUrl.replace(/\/$/, "")}/rss.xml`;

  try {
    const response = await fetch(rssUrl, { cache: "no-store" });
    if (!response.ok) {
      return {
        ok: false,
        itemCount: 0,
        detail: `RSS fetch failed: HTTP ${response.status}`,
      };
    }
    const xml = await response.text();
    const itemCount = (xml.match(/<item[\s>]/gi) ?? []).length;
    return {
      ok: itemCount > 0,
      itemCount,
      detail: itemCount === 0 ? "RSS channel has no <item> entries" : undefined,
    };
  } catch (error) {
    return {
      ok: false,
      itemCount: 0,
      detail: error instanceof Error ? error.message : String(error),
    };
  }
}
